import { useState, useEffect, useMemo } from 'react';
import { Sparkles, Globe, Target, AlertCircle, Plus, X, Check } from 'lucide-react';
import axios from 'axios';
import config from '../config';
import AucunCompteGoogleAds from './connectors/AucunCompteGoogleAds';

const API_URL = config.API_URL;

const KeywordPlannerTool = ({ token, selectedClientId, updateUrlWithAccount }) => {
  // États pour les comptes
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [loadingAccounts, setLoadingAccounts] = useState(true);

  // États pour les mots-clés existants (pour vérifier les doublons)
  const [existingKeywords, setExistingKeywords] = useState([]);
  const [loadingKeywords, setLoadingKeywords] = useState(false);

  // États pour le Keyword Planner
  const [kpUrl, setKpUrl] = useState('');
  const [kpSeedKeywords, setKpSeedKeywords] = useState('');
  const [kpLanguage, setKpLanguage] = useState('fr');
  const [kpCountry, setKpCountry] = useState('FR');
  const [kpLoading, setKpLoading] = useState(false);
  const [kpError, setKpError] = useState(null);
  const [kpIdeas, setKpIdeas] = useState([]);
  const [kpSortColumn, setKpSortColumn] = useState('avgMonthlySearches');
  const [kpSortDirection, setKpSortDirection] = useState('desc');
  const [kpStatusFilter, setKpStatusFilter] = useState('all'); // 'all', 'new', 'existing'

  // États pour le modal d'ajout de mot-clé
  const [addKeywordTerm, setAddKeywordTerm] = useState(null);
  const [addMatchType, setAddMatchType] = useState('EXACT');
  const [addSelectedAdGroups, setAddSelectedAdGroups] = useState([]);
  const [addLoading, setAddLoading] = useState(false);
  const [addError, setAddError] = useState(null);
  const [addSuccess, setAddSuccess] = useState(null);
  const [availableAdGroups, setAvailableAdGroups] = useState([]);
  const [loadingAdGroups, setLoadingAdGroups] = useState(false);

  // Charger les comptes au montage et quand le client change
  useEffect(() => {
    if (token) {
      setSelectedAccount('');
      loadAccounts();
    }
  }, [token, selectedClientId]);

  // Charger les mots-clés existants quand le compte est sélectionné
  useEffect(() => {
    if (token && selectedAccount) {
      fetchExistingKeywords();
    }
  }, [token, selectedAccount]);

  // Fonction pour charger la liste des comptes
  const loadAccounts = async () => {
    try {
      setLoadingAccounts(true);
      console.log('[KEYWORD-PLANNER] Chargement des comptes Google Ads...');

      const response = await axios.get(`${API_URL}/api/library?usage=outils`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success && response.data.library) {
        const data = response.data;

        const googleSheetsItems = data.library?.GOOGLE_SHEETS || [];
        const googleAdsItems = data.library?.GOOGLE_ADS || [];

        const adsItems = [
          ...googleSheetsItems.filter(item => item.type === 'GOOGLE_ADS'),
          ...googleAdsItems.filter(item => item.type === 'GOOGLE_ADS')
        ];

        let filteredAccounts = adsItems;
        if (selectedClientId) {
          filteredAccounts = await filterAccountsByClient(adsItems, selectedClientId);
        }

        // Trier les comptes par ordre alphabétique
        const sortedAccounts = [...filteredAccounts].sort((a, b) =>
          (a.name || '').localeCompare(b.name || '', 'fr', { sensitivity: 'base' })
        );
        setAccounts(sortedAccounts);

        // Sélectionner automatiquement si un seul compte
        if (sortedAccounts.length === 1) {
          const firstAccount = sortedAccounts[0];
          const accountId = firstAccount.sourceId || firstAccount.id;
          setSelectedAccount(accountId);
        }
      }
    } catch (error) {
      console.error('[KEYWORD-PLANNER] Erreur chargement comptes:', error);
    } finally {
      setLoadingAccounts(false);
    }
  };

  // Filtrer les comptes selon le client sélectionné
  const filterAccountsByClient = async (accounts, clientId) => {
    if (!clientId) return accounts;

    try {
      // Récupérer tous les sites et filtrer par clientId
      const response = await axios.get(`${API_URL}/api/clients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success && response.data.sites) {
        const clientSites = response.data.sites.filter(site => site.clientId === clientId);
        const authorizedAccountIds = clientSites.flatMap(site => site.accountIds || []);

        console.log('🎯 [FILTER KEYWORD-PLANNER] Sites du client:', clientSites.length);
        console.log('🎯 [FILTER KEYWORD-PLANNER] IDs comptes autorisés:', authorizedAccountIds);

        const filteredAccounts = accounts.filter(account => {
          const accountId = String(account.id);
          const sourceId = String(account.sourceId || '');
          return authorizedAccountIds.includes(accountId) || authorizedAccountIds.includes(sourceId);
        });

        console.log('🎯 [FILTER KEYWORD-PLANNER] Comptes filtrés:', filteredAccounts.length, '/', accounts.length);
        return filteredAccounts;
      }
    } catch (error) {
      console.error('❌ [FILTER KEYWORD-PLANNER] Erreur filtrage comptes:', error);
    }
    return accounts;
  };

  // Charger les mots-clés existants
  const fetchExistingKeywords = async () => {
    try {
      setLoadingKeywords(true);
      console.log('[KEYWORD-PLANNER] Chargement mots-clés existants pour:', selectedAccount);

      const response = await axios.get(
        `${API_URL}/api/keyword-opportunities/existing-keywords`,
        {
          headers: { 'Authorization': `Bearer ${token}` },
          params: { clientId: selectedAccount, period: 'LAST_90_DAYS' }
        }
      );

      console.log('[KEYWORD-PLANNER] Réponse existing-keywords:', response.data);

      if (response.data.success) {
        const keywords = response.data.keywords || [];
        console.log('[KEYWORD-PLANNER] Mots-clés chargés:', keywords.length);
        console.log('[KEYWORD-PLANNER] Exemples:', keywords.slice(0, 3));
        setExistingKeywords(keywords);
      } else {
        console.log('[KEYWORD-PLANNER] Réponse sans success:', response.data);
      }
    } catch (error) {
      console.error('[KEYWORD-PLANNER] Erreur chargement mots-clés:', error);
      console.error('[KEYWORD-PLANNER] Détails erreur:', error.response?.data);
    } finally {
      setLoadingKeywords(false);
    }
  };

  // Fonction pour générer des idées via le Keyword Planner
  const generateKeywordIdeas = async () => {
    if (!selectedAccount) {
      setKpError('Veuillez sélectionner un compte Google Ads');
      return;
    }

    if (!kpUrl && !kpSeedKeywords.trim()) {
      setKpError('Veuillez saisir une URL ou des mots-clés');
      return;
    }

    setKpLoading(true);
    setKpError(null);

    try {
      const seedKeywords = kpSeedKeywords
        .split(/[,\n]/)
        .map(k => k.trim())
        .filter(k => k.length > 0);

      // Charger les mots-clés existants en parallèle avec la génération d'idées
      console.log('[KEYWORD-PLANNER] Chargement mots-clés existants et génération idées...');

      const [ideasResponse, keywordsResponse] = await Promise.all([
        axios.post(
          `${API_URL}/api/keyword-opportunities/generate-ideas`,
          {
            clientId: selectedAccount,
            websiteUrl: kpUrl || null,
            seedKeywords: seedKeywords,
            languageCode: kpLanguage,
            countryCode: kpCountry
          },
          {
            headers: { 'Authorization': `Bearer ${token}` },
            timeout: 60000
          }
        ),
        axios.get(
          `${API_URL}/api/keyword-opportunities/existing-keywords`,
          {
            headers: { 'Authorization': `Bearer ${token}` },
            params: { clientId: selectedAccount }
          }
        )
      ]);

      const response = ideasResponse;
      const currentExistingKeywords = keywordsResponse.data.keywords || keywordsResponse.data || [];
      console.log('[KEYWORD-PLANNER] Mots-clés existants chargés:', currentExistingKeywords.length);

      if (response.data.success) {
        // Utiliser les mots-clés fraîchement chargés
        console.log('[KEYWORD-PLANNER] Mots-clés existants:', currentExistingKeywords.length);
        console.log('[KEYWORD-PLANNER] Exemples mots-clés existants:', currentExistingKeywords.slice(0, 5));

        const existingKeywordsLower = currentExistingKeywords
          .filter(kw => !kw.isNegative)
          .map(kw => (kw.text || kw.keyword || kw.criterionName || '').toLowerCase())
          .filter(text => text.length > 0);

        console.log('[KEYWORD-PLANNER] Mots-clés existants normalisés:', existingKeywordsLower.length);
        console.log('[KEYWORD-PLANNER] Exemples normalisés:', existingKeywordsLower.slice(0, 10));

        const ideasWithStatus = response.data.ideas.map(idea => ({
          ...idea,
          inAccount: existingKeywordsLower.includes(idea.keyword.toLowerCase())
        }));

        const inAccountCount = ideasWithStatus.filter(i => i.inAccount).length;
        console.log(`[KEYWORD-PLANNER] ${ideasWithStatus.length} idées générées, ${inAccountCount} déjà dans le compte`);

        setKpIdeas(ideasWithStatus);
      } else {
        setKpError(response.data.error || 'Erreur lors de la génération des idées');
      }
    } catch (err) {
      console.error('[KEYWORD-PLANNER] Erreur:', err);
      setKpError(err.response?.data?.details || err.response?.data?.error || err.message);
    } finally {
      setKpLoading(false);
    }
  };

  // Statistiques du Keyword Planner
  const kpStats = useMemo(() => {
    const total = kpIdeas.length;
    const inAccount = kpIdeas.filter(i => i.inAccount).length;
    const notInAccount = total - inAccount;
    return { total, inAccount, notInAccount };
  }, [kpIdeas]);

  // Filtrer et trier les idées
  const sortedKpIdeas = useMemo(() => {
    if (!kpIdeas.length) return [];

    let filtered = kpIdeas;
    if (kpStatusFilter === 'new') {
      filtered = kpIdeas.filter(i => !i.inAccount);
    } else if (kpStatusFilter === 'existing') {
      filtered = kpIdeas.filter(i => i.inAccount);
    }

    return [...filtered].sort((a, b) => {
      let aVal, bVal;

      switch (kpSortColumn) {
        case 'keyword':
          aVal = a.keyword.toLowerCase();
          bVal = b.keyword.toLowerCase();
          return kpSortDirection === 'asc'
            ? aVal.localeCompare(bVal)
            : bVal.localeCompare(aVal);
        case 'avgMonthlySearches':
          aVal = a.avgMonthlySearches || 0;
          bVal = b.avgMonthlySearches || 0;
          break;
        case 'competition':
          const compOrder = { 'LOW': 1, 'MEDIUM': 2, 'HIGH': 3, 'UNKNOWN': 0, 'UNSPECIFIED': 0 };
          aVal = compOrder[a.competition] || 0;
          bVal = compOrder[b.competition] || 0;
          break;
        case 'lowTopOfPageBid':
          aVal = a.lowTopOfPageBid || 0;
          bVal = b.lowTopOfPageBid || 0;
          break;
        case 'highTopOfPageBid':
          aVal = a.highTopOfPageBid || 0;
          bVal = b.highTopOfPageBid || 0;
          break;
        case 'inAccount':
          aVal = a.inAccount ? 1 : 0;
          bVal = b.inAccount ? 1 : 0;
          break;
        default:
          aVal = a[kpSortColumn] || 0;
          bVal = b[kpSortColumn] || 0;
      }

      return kpSortDirection === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [kpIdeas, kpSortColumn, kpSortDirection, kpStatusFilter]);

  // Fonctions pour le modal d'ajout
  const openAddKeywordModal = async (term) => {
    setAddKeywordTerm(term);
    setAddMatchType('EXACT');
    setAddSelectedAdGroups([]);
    setAddError(null);
    setAddSuccess(null);
    setLoadingAdGroups(true);

    try {
      const response = await axios.get(`${API_URL}/api/keyword-opportunities/ad-groups`, {
        params: { clientId: selectedAccount },
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data.success) {
        setAvailableAdGroups(response.data.adGroups || []);
      }
    } catch (err) {
      console.error('Erreur chargement adgroups:', err);
      setAddError('Impossible de charger les groupes d\'annonces');
    } finally {
      setLoadingAdGroups(false);
    }
  };

  const closeAddKeywordModal = () => {
    setAddKeywordTerm(null);
    setAddError(null);
    setAddSuccess(null);
  };

  const handleAddKeyword = async () => {
    if (!addKeywordTerm || addSelectedAdGroups.length === 0) {
      setAddError('Veuillez sélectionner au moins un groupe d\'annonces');
      return;
    }

    setAddLoading(true);
    setAddError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/keyword-opportunities/add-keyword`,
        {
          clientId: selectedAccount,
          keyword: addKeywordTerm.searchTerm || addKeywordTerm.keyword,
          matchType: addMatchType,
          adGroupIds: addSelectedAdGroups
        },
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setAddSuccess(`Mot-clé ajouté avec succès dans ${addSelectedAdGroups.length} groupe(s)`);

        // Mettre à jour le statut de l'idée
        const keyword = (addKeywordTerm.searchTerm || addKeywordTerm.keyword).toLowerCase();
        setKpIdeas(prev => prev.map(idea =>
          idea.keyword.toLowerCase() === keyword
            ? { ...idea, inAccount: true }
            : idea
        ));

        setTimeout(() => closeAddKeywordModal(), 2000);
      } else {
        setAddError(response.data.error || 'Erreur lors de l\'ajout');
      }
    } catch (err) {
      setAddError(err.response?.data?.error || err.message);
    } finally {
      setAddLoading(false);
    }
  };

  const toggleAdGroupSelection = (adGroupId) => {
    setAddSelectedAdGroups(prev =>
      prev.includes(adGroupId)
        ? prev.filter(id => id !== adGroupId)
        : [...prev, adGroupId]
    );
  };

  // Formater les nombres
  const formatNumber = (num) => {
    if (num === null || num === undefined) return '-';
    return new Intl.NumberFormat('fr-FR').format(num);
  };

  return (
    <div style={{ width: '100%', padding: '0' }}>
      {/* En-tête */}
      <div style={{
        background: 'white',
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '16px' }}>
          <div>
            <h2 style={{
              fontSize: '24px',
              fontWeight: '600',
              color: '#111827',
              display: 'flex',
              alignItems: 'center',
              margin: '0 0 8px 0'
            }}>
              <Sparkles style={{ marginRight: '8px', height: '24px', width: '24px', color: '#333333' }} />
              Générateur d'Idées de Mots-Clés
            </h2>
            <p style={{ color: '#6b7280', margin: '0', fontSize: '16px' }}>
              Découvrez de nouvelles opportunités de mots-clés grâce à l'API Google Ads Keyword Planner
            </p>
          </div>
        </div>
      </div>

      {/* Sélecteur de compte */}
      {/* Sans compte, la carte du sélecteur n'a rien à encadrer : on affiche
          l'état vide seul, plutôt que de le loger dans un cadre dans un cadre. */}
      {!loadingAccounts && accounts.length === 0 ? (
        <AucunCompteGoogleAds token={token} />
      ) : (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '20px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{ fontSize: '16px', fontWeight: '500', color: '#111827', margin: '0 0 12px 0' }}>
            Compte Google Ads
          </h3>

          {loadingAccounts ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <div style={{
                display: 'inline-block',
                width: '20px',
                height: '20px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #333333',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}></div>
              <p style={{ margin: '8px 0 0 0', color: '#6b7280' }}>Chargement des comptes...</p>
            </div>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151', minWidth: '100px' }}>
                Sélectionner :
              </label>
              <select
                value={selectedAccount}
                onChange={(e) => {
                  setSelectedAccount(e.target.value);
                  setKpIdeas([]);
                  if (updateUrlWithAccount) updateUrlWithAccount(e.target.value || null);
                }}
                style={{
                  flex: '1',
                  minWidth: '300px',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value="">-- Choisir un compte --</option>
                {accounts.map(account => {
                  const accountId = account.sourceId || account.id;
                  const accountName = account.name || account.displayName;
                  const displayName = accountName.includes(accountId) ? accountName : `${accountName} (${accountId})`;
                  return (
                    <option key={accountId} value={accountId}>
                      {displayName}
                    </option>
                  );
                })}
              </select>
            </div>
          )}
        </div>
      )}

      {/* Formulaire de génération */}
      {selectedAccount && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <h3 style={{
            fontSize: '18px',
            fontWeight: '600',
            color: '#111827',
            margin: '0 0 16px 0',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <Sparkles size={20} style={{ color: '#333333' }} />
            Générer des idées de mots-clés
          </h3>
          <p style={{
            color: '#6b7280',
            fontSize: '14px',
            margin: '0 0 20px 0'
          }}>
            Utilisez l'API Keyword Planner de Google pour découvrir de nouvelles opportunités de mots-clés non présents dans votre compte.
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px' }}>
            {/* URL du site */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                <Globe size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                URL du site web
              </label>
              <input
                type="url"
                value={kpUrl}
                onChange={(e) => setKpUrl(e.target.value)}
                placeholder="https://www.example.com"
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0 0' }}>
                Google analysera le contenu de la page pour suggérer des mots-clés pertinents
              </p>
            </div>

            {/* Mots-clés seed */}
            <div>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                <Target size={14} style={{ marginRight: '6px', verticalAlign: 'middle' }} />
                Mots-clés de départ
              </label>
              <textarea
                value={kpSeedKeywords}
                onChange={(e) => setKpSeedKeywords(e.target.value)}
                placeholder="Entrez vos mots-clés ici..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  resize: 'vertical',
                  boxSizing: 'border-box'
                }}
              />
              <p style={{ fontSize: '12px', color: '#9ca3af', margin: '4px 0 0 0' }}>
                Séparez les mots-clés par une virgule ou une nouvelle ligne (max 20)
              </p>
            </div>
          </div>

          {/* Paramètres de ciblage */}
          <div style={{
            display: 'flex',
            gap: '16px',
            marginTop: '20px',
            flexWrap: 'wrap',
            alignItems: 'flex-end'
          }}>
            <div style={{ minWidth: '150px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Langue
              </label>
              <select
                value={kpLanguage}
                onChange={(e) => setKpLanguage(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value="fr">Français</option>
                <option value="en">Anglais</option>
                <option value="de">Allemand</option>
                <option value="es">Espagnol</option>
                <option value="it">Italien</option>
                <option value="pt">Portugais</option>
                <option value="nl">Néerlandais</option>
              </select>
            </div>

            <div style={{ minWidth: '150px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>
                Pays cible
              </label>
              <select
                value={kpCountry}
                onChange={(e) => setKpCountry(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  background: 'white'
                }}
              >
                <option value="FR">France</option>
                <option value="BE">Belgique</option>
                <option value="CH">Suisse</option>
                <option value="CA">Canada</option>
                <option value="US">États-Unis</option>
                <option value="GB">Royaume-Uni</option>
                <option value="DE">Allemagne</option>
                <option value="ES">Espagne</option>
                <option value="IT">Italie</option>
              </select>
            </div>

            <button
              onClick={generateKeywordIdeas}
              disabled={kpLoading || (!kpUrl && !kpSeedKeywords.trim())}
              style={{
                padding: '10px 24px',
                background: kpLoading || (!kpUrl && !kpSeedKeywords.trim()) ? '#9ca3af' : '#333333',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: kpLoading || (!kpUrl && !kpSeedKeywords.trim()) ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                transition: 'background 0.2s'
              }}
            >
              {kpLoading ? (
                <>
                  <div style={{
                    width: '16px',
                    height: '16px',
                    border: '2px solid rgba(255,255,255,0.3)',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}></div>
                  Génération...
                </>
              ) : (
                <>
                  <Sparkles size={16} />
                  Générer des idées
                </>
              )}
            </button>
          </div>

          {/* Erreur */}
          {kpError && (
            <div style={{
              marginTop: '16px',
              padding: '12px 16px',
              background: '#fef2f2',
              border: '1px solid #fecaca',
              borderRadius: '8px',
              color: '#dc2626',
              fontSize: '14px',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <AlertCircle size={16} />
              {kpError}
            </div>
          )}
        </div>
      )}

      {/* Résultats */}
      {selectedAccount && kpIdeas.length > 0 && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '16px',
            flexWrap: 'wrap',
            gap: '12px'
          }}>
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: '#111827',
              margin: '0'
            }}>
              {formatNumber(sortedKpIdeas.length)} idées de mots-clés
              {kpStatusFilter !== 'all' && ` (sur ${formatNumber(kpStats.total)})`}
            </h3>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setKpStatusFilter('all')}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: kpStatusFilter === 'all' ? '#333333' : '#f3f4f6',
                  color: kpStatusFilter === 'all' ? 'white' : '#374151'
                }}
              >
                Tous ({formatNumber(kpStats.total)})
              </button>
              <button
                onClick={() => setKpStatusFilter('new')}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: kpStatusFilter === 'new' ? '#16a34a' : '#f3f4f6',
                  color: kpStatusFilter === 'new' ? 'white' : '#374151'
                }}
              >
                Nouveaux ({formatNumber(kpStats.notInAccount)})
              </button>
              <button
                onClick={() => setKpStatusFilter('existing')}
                style={{
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: kpStatusFilter === 'existing' ? '#dc2626' : '#f3f4f6',
                  color: kpStatusFilter === 'existing' ? 'white' : '#374151'
                }}
              >
                Dans le compte ({formatNumber(kpStats.inAccount)})
              </button>
            </div>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '14px'
            }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                  <th
                    onClick={() => {
                      if (kpSortColumn === 'keyword') {
                        setKpSortDirection(kpSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setKpSortColumn('keyword');
                        setKpSortDirection('asc');
                      }
                    }}
                    style={{ textAlign: 'left', padding: '12px 16px', color: '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                  >
                    Mot-clé {kpSortColumn === 'keyword' && (kpSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => {
                      if (kpSortColumn === 'avgMonthlySearches') {
                        setKpSortDirection(kpSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setKpSortColumn('avgMonthlySearches');
                        setKpSortDirection('desc');
                      }
                    }}
                    style={{ textAlign: 'right', padding: '12px 16px', color: '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                  >
                    Vol. mensuel {kpSortColumn === 'avgMonthlySearches' && (kpSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => {
                      if (kpSortColumn === 'competition') {
                        setKpSortDirection(kpSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setKpSortColumn('competition');
                        setKpSortDirection('desc');
                      }
                    }}
                    style={{ textAlign: 'center', padding: '12px 16px', color: '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                  >
                    Concurrence {kpSortColumn === 'competition' && (kpSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => {
                      if (kpSortColumn === 'lowTopOfPageBid') {
                        setKpSortDirection(kpSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setKpSortColumn('lowTopOfPageBid');
                        setKpSortDirection('desc');
                      }
                    }}
                    style={{ textAlign: 'right', padding: '12px 16px', color: '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                  >
                    CPC bas {kpSortColumn === 'lowTopOfPageBid' && (kpSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => {
                      if (kpSortColumn === 'highTopOfPageBid') {
                        setKpSortDirection(kpSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setKpSortColumn('highTopOfPageBid');
                        setKpSortDirection('desc');
                      }
                    }}
                    style={{ textAlign: 'right', padding: '12px 16px', color: '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                  >
                    CPC haut {kpSortColumn === 'highTopOfPageBid' && (kpSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => {
                      if (kpSortColumn === 'inAccount') {
                        setKpSortDirection(kpSortDirection === 'asc' ? 'desc' : 'asc');
                      } else {
                        setKpSortColumn('inAccount');
                        setKpSortDirection('asc');
                      }
                    }}
                    style={{ textAlign: 'center', padding: '12px 16px', color: '#6b7280', fontWeight: '500', cursor: 'pointer' }}
                  >
                    Statut {kpSortColumn === 'inAccount' && (kpSortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th style={{ textAlign: 'center', padding: '12px 16px', color: '#6b7280', fontWeight: '500' }}>
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedKpIdeas.map((idea, index) => (
                  <tr
                    key={index}
                    style={{ borderBottom: '1px solid #f3f4f6', transition: 'background 0.15s' }}
                    onMouseEnter={(e) => e.currentTarget.style.background = '#f9fafb'}
                    onMouseLeave={(e) => e.currentTarget.style.background = 'white'}
                  >
                    <td style={{ padding: '12px 16px', fontWeight: '500', color: '#111827' }}>
                      {idea.keyword}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                      {formatNumber(idea.avgMonthlySearches)}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: idea.competition === 'LOW' ? '#dcfce7' :
                          idea.competition === 'MEDIUM' ? '#fef3c7' :
                          idea.competition === 'HIGH' ? '#fecaca' : '#f3f4f6',
                        color: idea.competition === 'LOW' ? '#166534' :
                          idea.competition === 'MEDIUM' ? '#92400e' :
                          idea.competition === 'HIGH' ? '#dc2626' : '#6b7280'
                      }}>
                        {idea.competition === 'LOW' ? 'Faible' :
                          idea.competition === 'MEDIUM' ? 'Moyenne' :
                          idea.competition === 'HIGH' ? 'Élevée' : '-'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                      {idea.lowTopOfPageBid ? `${idea.lowTopOfPageBid.toFixed(2)} €` : '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'right', color: '#374151' }}>
                      {idea.highTopOfPageBid ? `${idea.highTopOfPageBid.toFixed(2)} €` : '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 10px',
                        borderRadius: '9999px',
                        fontSize: '12px',
                        fontWeight: '500',
                        background: idea.inAccount ? '#fef2f2' : '#dcfce7',
                        color: idea.inAccount ? '#dc2626' : '#16a34a',
                        border: `1px solid ${idea.inAccount ? '#fecaca' : '#bbf7d0'}`
                      }}>
                        {idea.inAccount ? 'Dans le compte' : 'Nouveau'}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <button
                        onClick={() => openAddKeywordModal({ searchTerm: idea.keyword })}
                        style={{
                          padding: '6px 12px',
                          background: '#333333',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: '500',
                          cursor: 'pointer',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px'
                        }}
                      >
                        <Plus size={14} />
                        Ajouter
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Message si pas de résultats */}
      {selectedAccount && !kpLoading && kpIdeas.length === 0 && (kpUrl || kpSeedKeywords) && !kpError && (
        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px 24px',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
          textAlign: 'center'
        }}>
          <Sparkles size={48} style={{ color: '#d1d5db', marginBottom: '16px' }} />
          <p style={{ color: '#6b7280', fontSize: '16px', margin: 0 }}>
            Cliquez sur "Générer des idées" pour découvrir de nouvelles opportunités
          </p>
        </div>
      )}

      {/* Modal d'ajout de mot-clé */}
      {addKeywordTerm && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}
          onClick={closeAddKeywordModal}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              padding: '24px',
              maxWidth: '600px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête du modal */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              marginBottom: '20px'
            }}>
              <div>
                <h3 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                  Ajouter comme mot-clé
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '16px',
                  fontWeight: '500',
                  color: '#374151',
                  backgroundColor: '#dcfce7',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  display: 'inline-block'
                }}>
                  "{addKeywordTerm.searchTerm || addKeywordTerm.keyword}"
                </p>
              </div>
              <button
                onClick={closeAddKeywordModal}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '6px',
                  color: '#6b7280'
                }}
                onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
              >
                <X size={20} />
              </button>
            </div>

            {/* Message de succès */}
            {addSuccess && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#dcfce7',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Check size={20} style={{ color: '#16a34a' }} />
                <span style={{ color: '#166534', fontSize: '14px' }}>{addSuccess}</span>
              </div>
            )}

            {/* Message d'erreur */}
            {addError && (
              <div style={{
                padding: '12px 16px',
                backgroundColor: '#fef2f2',
                borderRadius: '8px',
                marginBottom: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <AlertCircle size={20} style={{ color: '#dc2626' }} />
                <span style={{ color: '#991b1b', fontSize: '14px' }}>{addError}</span>
              </div>
            )}

            {/* Type de correspondance */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '8px'
              }}>
                Type de correspondance
              </label>
              <div style={{ display: 'flex', gap: '8px' }}>
                {[
                  { value: 'EXACT', label: 'Exact', preview: `[${addKeywordTerm.searchTerm || addKeywordTerm.keyword}]` },
                  { value: 'PHRASE', label: 'Expression', preview: `"${addKeywordTerm.searchTerm || addKeywordTerm.keyword}"` },
                  { value: 'BROAD', label: 'Large', preview: addKeywordTerm.searchTerm || addKeywordTerm.keyword }
                ].map((type) => (
                  <button
                    key={type.value}
                    onClick={() => setAddMatchType(type.value)}
                    style={{
                      flex: 1,
                      padding: '12px',
                      border: addMatchType === type.value ? '2px solid #333333' : '1px solid #d1d5db',
                      borderRadius: '8px',
                      background: addMatchType === type.value ? '#FAF9F5' : 'white',
                      cursor: 'pointer',
                      textAlign: 'center'
                    }}
                  >
                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                      {type.label}
                    </div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                      {type.preview}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Sélection des groupes d'annonces */}
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '12px'
              }}>
                Groupes d'annonces cibles ({addSelectedAdGroups.length} sélectionné{addSelectedAdGroups.length > 1 ? 's' : ''})
              </label>

              <div>
                <div style={{
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#6b7280',
                  marginBottom: '8px'
                }}>
                  Groupes d'annonces disponibles
                </div>

                {loadingAdGroups ? (
                  <div style={{ padding: '20px', textAlign: 'center' }}>
                    <div style={{
                      display: 'inline-block',
                      width: '20px',
                      height: '20px',
                      border: '2px solid #f3f3f3',
                      borderTop: '2px solid #333333',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    <p style={{ margin: '8px 0 0 0', color: '#6b7280', fontSize: '14px' }}>Chargement des groupes d'annonces...</p>
                  </div>
                ) : availableAdGroups.length === 0 ? (
                  <div style={{
                    padding: '16px',
                    backgroundColor: '#f9fafb',
                    borderRadius: '8px',
                    textAlign: 'center',
                    color: '#6b7280',
                    fontSize: '14px'
                  }}>
                    Aucun groupe d'annonces disponible
                  </div>
                ) : (
                  <div style={{
                    maxHeight: '200px',
                    overflowY: 'auto',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px'
                  }}>
                    {/* Regrouper par campagne */}
                    {Object.entries(
                      availableAdGroups.reduce((acc, ag) => {
                        const key = ag.campaignName || 'Sans campagne';
                        if (!acc[key]) acc[key] = [];
                        acc[key].push(ag);
                        return acc;
                      }, {})
                    ).map(([campaignName, adGroups]) => (
                      <div key={campaignName}>
                        <div style={{
                          padding: '8px 12px',
                          backgroundColor: '#f9fafb',
                          fontSize: '12px',
                          fontWeight: '600',
                          color: '#6b7280',
                          borderBottom: '1px solid #e5e7eb'
                        }}>
                          {campaignName}
                        </div>
                        {adGroups.map((ag) => (
                          <div
                            key={ag.id}
                            onClick={() => toggleAdGroupSelection(ag.id)}
                            style={{
                              padding: '10px 12px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              cursor: 'pointer',
                              backgroundColor: addSelectedAdGroups.includes(ag.id) ? '#FAF9F5' : 'white',
                              borderBottom: '1px solid #f3f4f6'
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={addSelectedAdGroups.includes(ag.id)}
                              onChange={() => {}}
                              style={{ cursor: 'pointer' }}
                            />
                            <span style={{ fontSize: '14px', color: '#111827' }}>{ag.name}</span>
                            {ag.status !== 'ENABLED' && (
                              <span style={{
                                fontSize: '10px',
                                padding: '2px 6px',
                                backgroundColor: '#fef3c7',
                                color: '#92400e',
                                borderRadius: '4px'
                              }}>
                                {ag.status}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Boutons d'action */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
              <button
                onClick={closeAddKeywordModal}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500'
                }}
                onMouseEnter={(e) => e.target.style.background = '#e5e7eb'}
                onMouseLeave={(e) => e.target.style.background = '#f3f4f6'}
              >
                Annuler
              </button>
              <button
                onClick={handleAddKeyword}
                disabled={addLoading || addSelectedAdGroups.length === 0 || addSuccess}
                style={{
                  padding: '10px 20px',
                  background: addLoading || addSelectedAdGroups.length === 0 || addSuccess ? '#9ca3af' : '#16a34a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: addLoading || addSelectedAdGroups.length === 0 || addSuccess ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                {addLoading ? (
                  <>
                    <div style={{
                      width: '16px',
                      height: '16px',
                      border: '2px solid rgba(255,255,255,0.3)',
                      borderTop: '2px solid white',
                      borderRadius: '50%',
                      animation: 'spin 1s linear infinite'
                    }}></div>
                    Ajout en cours...
                  </>
                ) : addSuccess ? (
                  <>
                    <Check size={16} />
                    Ajouté
                  </>
                ) : (
                  <>
                    <Plus size={16} />
                    Ajouter le mot-clé
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Style pour l'animation de spin */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default KeywordPlannerTool;
